<template>
  <ExpandableListItem
    ref="expandableListItemRef"
    :color="data.color"
    :expanded="expanded"
    :items="listItems"
    draggable
    :dragDisabled="device.touch"
    itemKey="id"
    testId="service-category"
    :moreOptions="['edit', 'delete', 'sort']"
    :showChevron="listLength > 1"
    @click="onClick"
    @update:items="listItems = $event"
    @moreSelect="onMoreSelect"
  >
    <template #left>
      <BaseText
        bold
        :color="data.color ? 'fixed' : undefined"
        v-test="'service-category-name'"
      >
        {{ data.name }}
      </BaseText>
      <BaseText
        :color="data.color ? 'fixed' : undefined"
        v-test="'service-category-amount'"
      >
        ({{ data.services.length }})
      </BaseText>
    </template>
    <template
      v-if="serviceCategorySortOder"
      #actions
    >
      <div :class="$style.sortOrder">
        <BaseCounter
          v-model="sortOrderValue"
          :minValue="0"
          :maxValue="listLength - 1"
          hideValue
          reverse
          v-test="'category-sort-order'"
        />
      </div>
    </template>
    <template #row="{ item, index }">
      <Group
        :serviceGroup="item"
        :index="index"
        :listLength="listItems.length"
        @updateSortOrder="sortByClick($event, item)"
        v-test="'group-category-service'"
      />
    </template>
    <template #empty>
      <div
        :class="$style.filler"
        v-test="'group-category-empty'"
      >
        {{ $t('services.category_no_services') }}
      </div>
    </template>
    <template #footer>
      <RouterLink
        :class="[$style.filler, $style.addService]"
        :to="{
          name: 'admin-service-new',
          params: { categoryId: data.id },
        }"
        v-test="'service-category-add-service'"
      >
        <BaseIcon
          name="plus"
          color="success"
        />
        <BaseText bold>{{ $t('services.add_service_to_category') }}</BaseText>
      </RouterLink>
    </template>
  </ExpandableListItem>
</template>

<script
  lang="ts"
  setup
>
import { computed, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { flash, modal } from '@/helpers/ui';
import {
  sortByClick as sortByClickHelper,
  sortByDrag,
} from '@/modules/admin/helpers';
import ExpandableListItem from '@/modules/admin/services/shared/ExpandableListItem.vue';
import { useServiceGroupsStore } from '@/stores/service-groups';
import { useServicesStore } from '@/stores/services';
import type { Service, ServiceCategory } from '@/types';
import { device } from '@/user-context';
import Group from './Group.vue';

type MoreAction = 'edit' | 'delete' | 'sort';

const router = useRouter();
const { t } = useI18n();

const props = defineProps<{
  data: ServiceCategory;
  expanded: boolean;
  listLength: number;
  index: number;
}>();

const emit = defineEmits<{
  (e: 'updateSortOrder', value: number): void;
  (e: 'toggle', element: HTMLElement): void;
}>();

const { sortServiceGroups } = useServiceGroupsStore();

const expandableListItemRef = ref<InstanceType<
  typeof ExpandableListItem
> | null>(null);

const onClick = () => {
  emit('toggle', expandableListItemRef.value?.$el);
};

const listItems = computed<Service[]>({
  get: () => props.data.services,
  set: (newValue) => {
    sortServiceGroups(sortByDrag(newValue, listItems.value)).then(() => {
      flash(t('global.flash.services_updated'));
    });
  },
});

const sortByClick = (newIndex: number, service: Service) => {
  sortServiceGroups(sortByClickHelper(newIndex, service, listItems.value)).then(
    () => {
      flash(t('global.flash.services_updated'));
    },
  );
};

const serviceCategorySortOder = inject(
  'services-category-sort-order',
  ref(false),
);

const sortOrderValue = computed({
  get: () => props.index,
  set: (value: number) => emit('updateSortOrder', value),
});

const editCategory = () => {
  router.push({
    name: 'admin-service-category',
    params: {
      categoryId: props.data.id,
    },
  });
};

const deleteCategory = () => {
  if (props.data.services && props.data.services.length > 0) {
    modal('warning', {
      message: t('global.warning.delete', { item: props.data.name }),
      subMessage: t('category.delete_warning'),
    });
  } else {
    modal('confirmation', {
      type: 'delete',
      item: props.data.name,
    }).then(() => {
      const { deleteCategory } = useServicesStore();
      deleteCategory(props.data.id).catch(() => {
        modal('warning', {
          message: t('global.warning.delete', {
            item: props.data.name,
          }),
          subMessage: t('category.delete_warning'),
        });
      });
    });
  }
};

const onMoreSelect = (action: string) => {
  switch (action as MoreAction) {
    case 'edit':
      editCategory();
      break;
    case 'delete':
      deleteCategory();
      break;
    case 'sort':
      serviceCategorySortOder.value = true;
      break;
  }
};
</script>

<style
  lang="scss"
  module
>
.filler {
  display: flex;
  align-items: center;
  gap: $spacing * 0.5;
  padding: $spacing;
  width: 100%;
}

.addService {
  cursor: pointer;
}

.sortOrder {
  margin-top: $spacing * -0.5;
  margin-bottom: $spacing * -0.5;
}
</style>
